import { useQuery } from '@tanstack/react-query';

import { GetLeaguesDetailsRes } from '@/app/(default)/(leagues)/leagues/api/getLeagueDetails/route';
import { GetLeaguesDetailsScheduleRes } from '@/app/(default)/(leagues)/leagues/api/getLeagueDetailsSchedule/route';
import { GetLeaguesDetailsSponsorsRes } from '@/app/(default)/(leagues)/leagues/api/getLeagueDetailsSponsors/route';
import { GetLeaguesDetailsStandingsRes } from '@/app/(default)/(leagues)/leagues/api/getLeagueDetailsStandings/route';
import { GetLeaguesRegistrationWithQRCodeInfoRes } from '@/app/(default)/(leagues)/leagues/api/getLeagueRegistrationWithQRCodeInfo/route';
import { NEXT_PUBLIC_PL_KEY } from '@/modules/league/new-components/constants';

export const useGetLeagueDetailsHeaderQueryData = (slug: string, sessionId: string) => {
  return useQuery({
    queryKey: ['league_details_header', slug, sessionId],
    queryFn: async () => {
      try {
        const response = await fetch(
          `/leagues/api/getLeagueDetails?slug=${slug}&sessionId=${sessionId}`,
          {
            headers: {
              'X-PL-PUBLIC-KEY': `${NEXT_PUBLIC_PL_KEY}`,
            },
          }
        );
        const responseData: { data: GetLeaguesDetailsRes } = await response.json();

        return responseData.data;
      } catch {
        return null;
      }
    },
    staleTime: 30 * 1000,
  });
};

export const useGetLeagueStandingsQueryData = (sessionId: string, rankType: string) => {
  return useQuery({
    queryKey: ['league_details_standings', rankType, sessionId],
    queryFn: async () => {
      try {
        const response = await fetch(
          `/leagues/api/getLeagueDetailsStandings?sessionId=${sessionId}&rankType=${rankType}`,
          {
            headers: {
              'X-PL-PUBLIC-KEY': `${NEXT_PUBLIC_PL_KEY}`,
            },
          }
        );
        const responseData: { data: GetLeaguesDetailsStandingsRes } = await response.json();

        return responseData.data;
      } catch {
        return null;
      }
    },
    staleTime: 30 * 1000,
  });
};

export const useGetLeagueScheduleQueryData = (sessionId: string) => {
  return useQuery({
    queryKey: ['league_details_schedule', sessionId],
    queryFn: async () => {
      try {
        const response = await fetch(
          `/leagues/api/getLeagueDetailsSchedule?sessionId=${sessionId}`,
          {
            headers: {
              'X-PL-PUBLIC-KEY': `${NEXT_PUBLIC_PL_KEY}`,
            },
          }
        );
        const responseData: { data: GetLeaguesDetailsScheduleRes } = await response.json();

        return responseData.data;
      } catch {
        return null;
      }
    },
    staleTime: 30 * 1000,
  });
};

export const useGetLeagueSponsorsQueryData = (sessionId: string) => {
  return useQuery({
    queryKey: ['league_details_sponsors', sessionId],
    queryFn: async () => {
      try {
        const response = await fetch(
          `/leagues/api/getLeagueDetailsSponsors?sessionId=${sessionId}`,
          {
            headers: {
              'X-PL-PUBLIC-KEY': `${NEXT_PUBLIC_PL_KEY}`,
            },
          }
        );
        const responseData: { data: GetLeaguesDetailsSponsorsRes } = await response.json();

        return responseData.data;
      } catch {
        return null;
      }
    },
    staleTime: 30 * 1000,
  });
};

export const useGetLeagueRegistrationWithWRCodeInfoQueryData = (
  slug: string,
  sessionId: string
) => {
  return useQuery({
    queryKey: ['league_registration_with_qr_code_info', slug, sessionId],
    queryFn: async () => {
      try {
        const response = await fetch(
          `/leagues/api/getLeagueRegistrationWithQRCodeInfo?slug=${slug}&sessionId=${sessionId}`,
          {
            headers: {
              'X-PL-PUBLIC-KEY': `${NEXT_PUBLIC_PL_KEY}`,
            },
          }
        );
        const responseData: { data: GetLeaguesRegistrationWithQRCodeInfoRes } =
          await response.json();

        return responseData.data;
      } catch {
        return null;
      }
    },
    staleTime: 30 * 1000,
  });
};

export const useGetLeagueQRCodeQueryData = (
  leagueId: string,
  sessionId: string,
  clubId: string
) => {
  return useQuery({
    queryKey: ['league_qr_code_data', leagueId, sessionId, clubId],
    queryFn: async () => {
      try {
        const response = await fetch(
          `/leagues/api/getLeagueQRCode?leagueId=${leagueId}&sessionId=${sessionId}&clubId=${clubId}`,
          {
            headers: {
              'X-PL-PUBLIC-KEY': `${NEXT_PUBLIC_PL_KEY}`,
            },
          }
        );
        const responseData: { data: string } = await response.json();

        return responseData.data;
      } catch {
        return null;
      }
    },
    staleTime: 24 * 60 * 60,
  });
};
